import { useEffect, useState, useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import { Box } from '@mui/material'

import { getAllApplicant } from 'services/api/modules/recruitment/job-application'
import { getOneJobAds } from 'services/api/modules/recruitment/job-advertisment'
import { initFilterState, initSearchState, initAppIdState } from '../helpers/initState'
import RecruitmentTabs from './RecruitmentTabs'
import CandidateTable from './CandidateTable'
import usePagination from '../hooks/usePagination'

const BodyManageApplicant = () => {
  // Get type from URL search params, default to 0
  const [searchParams, setSearchParams] = useSearchParams({ type: 0 })
  const type = parseInt(searchParams.get('type'), 10)
  const { id } = useParams() // Get job ID from URL params

  /*
  State to manage the active tab
  value: 
  0  - Matched candidates
  1  - Applied candidates
  3  - Shortlisted candidates
  5  - Interviewed candidates
  7  - Offered candidates
  9  - Hired candidates
  10 - Rejected candidates
  */
  const [value, setValue] = useState(type)

  // Handle tab change
  const handleChange = useCallback(
    (event, newValue) => {
      setValue(newValue)
      setSearchParams({ type: newValue })
    },
    [setSearchParams]
  )

  // Pagination states for different tabs
  const matchedPagination = usePagination()
  const appliedPagination = usePagination()
  const shortlistedPagination = usePagination()
  const interviewedPagination = usePagination()
  const offeredPagination = usePagination()
  const hiredPagination = usePagination()
  const rejectedPagination = usePagination()

  // State for filters and searches
  const [filterStates, setFilterStates] = useState(initFilterState)
  const [searchStates, setSearchStates] = useState(initSearchState)
  const [applicationIdStates, setApplicationIdStates] = useState(initAppIdState)

  // Reset filters and search states on tab change
  useEffect(() => {
    setFilterStates(initFilterState)
    setApplicationIdStates(initSearchState)
    setSearchStates(initAppIdState)
  }, [type])

  // Query client to manage cache
  const queryClient = useQueryClient()

  // Invalidate and reset queries on component mount
  useEffect(() => {
    queryClient.invalidateQueries('getAllApplicant')
    queryClient.resetQueries('getAllApplicant')
  }, [queryClient])

  /*
    Status types for candidates
    1 - Matched candidates (status: 3)
    1 - Applied candidates (combined status: 1,2,4,5)
    2 - Shortlisted candidates
    3 - Interviewed candidates
    4 - Offered candidates
    5 - Hired candidates
    6 - Rejected candidates
  */

  // Query for matched candidates
  const matchedQuery = useQuery(
    [
      'getAllApplicant',
      matchedPagination.page,
      'matched',
      matchedPagination.rowsPerPage,
      filterStates.matched,
      searchStates.matched,
    ],
    async () => {
      const { data } = await getAllApplicant(
        id,
        1,
        '3',
        matchedPagination.rowsPerPage,
        matchedPagination.page + 1,
        filterStates.matched,
        searchStates.matched
      )
      return data
    },
    { refetchOnWindowFocus: false, enabled: value === 0 }
  )

  // Query for applied candidates
  const appliedQuery = useQuery(
    [
      'getAllApplicant',
      appliedPagination.page,
      'applied',
      appliedPagination.rowsPerPage,
      filterStates.applied,
      searchStates.applied,
    ],
    async () => {
      const { data } = await getAllApplicant(
        id,
        1,
        '1,2,4,5',
        appliedPagination.rowsPerPage,
        appliedPagination.page + 1,
        filterStates.applied,
        searchStates.applied
      )
      return data
    },
    { refetchOnWindowFocus: false, enabled: value === 1 }
  )

  // Query for shortlisted candidates
  const shortlistedQuery = useQuery(
    [
      'getAllApplicant',
      shortlistedPagination.page,
      'shortlisted',
      shortlistedPagination.rowsPerPage,
      filterStates.shortlisted,
      searchStates.shortlisted,
      applicationIdStates.shortlisted,
    ],
    async () => {
      const { data } = await getAllApplicant(
        id,
        2,
        applicationIdStates.shortlisted !== '' ? applicationIdStates.shortlisted : '1,2,3,4,5',
        shortlistedPagination.rowsPerPage,
        shortlistedPagination.page + 1,
        filterStates.shortlisted,
        searchStates.shortlisted
      )
      return data
    },
    { refetchOnWindowFocus: false, enabled: value === 3 }
  )

  // Query for interviewed candidates
  const interviewedQuery = useQuery(
    [
      'getAllApplicant',
      interviewedPagination.page,
      'interviewed',
      interviewedPagination.rowsPerPage,
      filterStates.interviewed,
      searchStates.interviewed,
      applicationIdStates.interviewed,
    ],
    async () => {
      const { data } = await getAllApplicant(
        id,
        3,
        applicationIdStates.interviewed !== '' ? applicationIdStates.interviewed : '1,2,3,4,5',
        interviewedPagination.rowsPerPage,
        interviewedPagination.page + 1,
        filterStates.interviewed,
        searchStates.interviewed
      )
      return data
    },
    { refetchOnWindowFocus: false, enabled: value === 5 }
  )

  // Query for offered candidates
  const offeredQuery = useQuery(
    [
      'getAllApplicant',
      offeredPagination.page,
      'offered',
      offeredPagination.rowsPerPage,
      filterStates.offered,
      searchStates.offered,
      applicationIdStates.offered,
    ],
    async () => {
      const { data } = await getAllApplicant(
        id,
        4,
        applicationIdStates.offered !== '' ? applicationIdStates.offered : '1,2,3,4,5',
        offeredPagination.rowsPerPage,
        offeredPagination.page + 1,
        filterStates.offered,
        searchStates.offered
      )
      return data
    },
    { refetchOnWindowFocus: false, enabled: value === 7 }
  )

  // Query for hired candidates
  const hiredQuery = useQuery(
    [
      'getAllApplicant',
      hiredPagination.page,
      'hired',
      hiredPagination.rowsPerPage,
      filterStates.hired,
      searchStates.hired,
      applicationIdStates.hired,
    ],
    async () => {
      const { data } = await getAllApplicant(
        id,
        5,
        applicationIdStates.hired !== '' ? applicationIdStates.hired : '1,2,3,4,5',
        hiredPagination.rowsPerPage,
        hiredPagination.page + 1,
        filterStates.hired,
        searchStates.hired
      )
      return data
    },
    { refetchOnWindowFocus: false, enabled: value === 9 }
  )

  // Query for rejected candidates
  const rejectedQuery = useQuery(
    [
      'getAllApplicant',
      rejectedPagination.page,
      'rejected',
      rejectedPagination.rowsPerPage,
      filterStates.rejected,
      searchStates.rejected,
      applicationIdStates.rejected,
    ],
    async () => {
      const { data } = await getAllApplicant(
        id,
        6,
        applicationIdStates.rejected !== '' ? applicationIdStates.rejected : '1,2,3,4,5',
        rejectedPagination.rowsPerPage,
        rejectedPagination.page + 1,
        filterStates.rejected,
        searchStates.rejected
      )
      return data
    },
    { refetchOnWindowFocus: false, enabled: value === 10 }
  )

  // Query to get specific job statistics
  const jobStats = useQuery(['jobstats', id], async () => {
    const { data } = await getOneJobAds(id)
    return data
  })

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'gray-line.main',
        borderRadius: '4.5px',
        bgcolor: 'white.main',
      }}
    >
      <RecruitmentTabs value={value} handleChange={handleChange} jobStats={jobStats} />
      <Box sx={{ overflowY: 'auto' }}>
        {value === 0 && (
          <CandidateTable
            type="matched"
            jobId={id}
            dataCandidate={matchedQuery.data?.data}
            isLoading={matchedQuery.isLoading}
            error={matchedQuery.error}
            lastPage={matchedQuery.data?.last_page}
            count={matchedQuery.data?.total}
            handleChangePage={matchedPagination.handleChangePage}
            page={matchedPagination.page}
            rowsPerPage={matchedPagination.rowsPerPage}
            handleChangeRows={matchedPagination.handleChangeRowsPerPage}
            setFilter={(value) => setFilterStates((prev) => ({ ...prev, matched: value }))}
            setSearch={(value) => setSearchStates((prev) => ({ ...prev, matched: value }))}
            search={searchStates.matched}
          />
        )}
        {value === 1 && (
          <CandidateTable
            type="applied"
            jobId={id}
            dataCandidate={appliedQuery.data?.data}
            isLoading={appliedQuery.isLoading}
            error={appliedQuery.error}
            lastPage={appliedQuery.data?.last_page}
            count={appliedQuery.data?.total}
            handleChangePage={appliedPagination.handleChangePage}
            page={appliedPagination.page}
            rowsPerPage={appliedPagination.rowsPerPage}
            handleChangeRows={appliedPagination.handleChangeRowsPerPage}
            setFilter={(value) => setFilterStates((prev) => ({ ...prev, applied: value }))}
            setSearch={(value) => setSearchStates((prev) => ({ ...prev, applied: value }))}
          />
        )}
        {value === 3 && (
          <CandidateTable
            type="shortlisted"
            jobId={id}
            dataCandidate={shortlistedQuery.data?.data}
            isLoading={shortlistedQuery.isLoading}
            error={shortlistedQuery.error}
            lastPage={shortlistedQuery.data?.last_page}
            count={shortlistedQuery.data?.total}
            page={shortlistedPagination.page}
            rowsPerPage={shortlistedPagination.rowsPerPage}
            handleChangePage={shortlistedPagination.handleChangePage}
            handleChangeRows={shortlistedPagination.handleChangeRowsPerPage}
            setFilter={(value) => setFilterStates((prev) => ({ ...prev, shortlisted: value }))}
            setSearch={(value) => setSearchStates((prev) => ({ ...prev, shortlisted: value }))}
            setApplicationId={(value) =>
              setApplicationIdStates((prev) => ({ ...prev, shortlisted: value }))
            }
          />
        )}
        {value === 5 && (
          <CandidateTable
            type="interview"
            jobId={id}
            dataCandidate={interviewedQuery.data?.data}
            isLoading={interviewedQuery.isLoading}
            error={interviewedQuery.error}
            lastPage={interviewedQuery.data?.last_page}
            count={interviewedQuery.data?.total}
            page={interviewedPagination.page}
            rowsPerPage={interviewedPagination.rowsPerPage}
            handleChangePage={interviewedPagination.handleChangePage}
            handleChangeRows={interviewedPagination.handleChangeRowsPerPage}
            setFilter={(value) => setFilterStates((prev) => ({ ...prev, interviewed: value }))}
            setSearch={(value) => setSearchStates((prev) => ({ ...prev, interviewed: value }))}
            setApplicationId={(value) =>
              setApplicationIdStates((prev) => ({ ...prev, interviewed: value }))
            }
          />
        )}
        {value === 7 && (
          <CandidateTable
            type="offered"
            jobId={id}
            dataCandidate={offeredQuery.data?.data}
            isLoading={offeredQuery.isLoading}
            error={offeredQuery.error}
            lastPage={offeredQuery.data?.last_page}
            count={offeredQuery.data?.total}
            page={offeredPagination.page}
            rowsPerPage={offeredPagination.rowsPerPage}
            handleChangePage={offeredPagination.handleChangePage}
            handleChangeRows={offeredPagination.handleChangeRowsPerPage}
            setFilter={(value) => setFilterStates((prev) => ({ ...prev, offered: value }))}
            setSearch={(value) => setSearchStates((prev) => ({ ...prev, offered: value }))}
            setApplicationId={(value) =>
              setApplicationIdStates((prev) => ({ ...prev, offered: value }))
            }
          />
        )}
        {value === 9 && (
          <CandidateTable
            type="hired"
            jobId={id}
            dataCandidate={hiredQuery.data?.data}
            isLoading={hiredQuery.isLoading}
            error={hiredQuery.error}
            lastPage={hiredQuery.data?.last_page}
            count={hiredQuery.data?.total}
            page={hiredPagination.page}
            rowsPerPage={hiredPagination.rowsPerPage}
            handleChangePage={hiredPagination.handleChangePage}
            handleChangeRows={hiredPagination.handleChangeRowsPerPage}
            setFilter={(value) => setFilterStates((prev) => ({ ...prev, hired: value }))}
            setSearch={(value) => setSearchStates((prev) => ({ ...prev, hired: value }))}
            setApplicationId={(value) =>
              setApplicationIdStates((prev) => ({ ...prev, hired: value }))
            }
          />
        )}
        {value === 10 && (
          <CandidateTable
            type="rejected"
            jobId={id}
            dataCandidate={rejectedQuery.data?.data}
            isLoading={rejectedQuery.isLoading}
            error={rejectedQuery.error}
            lastPage={rejectedQuery.data?.last_page}
            count={rejectedQuery.data?.total}
            page={rejectedPagination.page}
            rowsPerPage={rejectedPagination.rowsPerPage}
            handleChangePage={rejectedPagination.handleChangePage}
            handleChangeRows={rejectedPagination.handleChangeRowsPerPage}
            setFilter={(value) => setFilterStates((prev) => ({ ...prev, rejected: value }))}
            setSearch={(value) => setSearchStates((prev) => ({ ...prev, rejected: value }))}
            setApplicationId={(value) =>
              setApplicationIdStates((prev) => ({ ...prev, rejected: value }))
            }
          />
        )}
      </Box>
    </Box>
  )
}

export default BodyManageApplicant
